import React from 'react';
import { Button, Box, Dialog, DialogTitle, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import { LangContext } from '../../contexts/lang.context';
import { DispatchContext } from '../../contexts/userInfo.context';
import { ActionTypes } from '../../actions';
import { HintText, SectionTitle, MyTextField, MyButton } from '../ui';
import { Contact } from '../../models/interfaces';
import * as model from '../../models/api';

interface EditDialogProps {
  open: boolean;
  handleClose: () => void;
  contact: Contact;
}

export const ContactDialog = (props: EditDialogProps) => {
  const { open, handleClose, contact } = props;
  const { APP, DIALOG, BUTTON } = React.useContext(LangContext).currentLangData;
  const dispatch = React.useContext(DispatchContext);

  const usernameDefault = contact.username === '' ? contact.userId : contact.username;

  const [isEdittingUsername, setEdittingUsername] = React.useState(false);
  const [isProgress, setIsProgress] = React.useState(false);
  const [username, setUsername] = React.useState<string>(usernameDefault);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
    setUsername((event.target as HTMLInputElement).value);

  const handleDelete = async () => {
    handleClose();
    const isSuccess = await model.deleteContact(contact.userId);
    if (isSuccess) {
      console.log('delete contact success');
      dispatch({ type: ActionTypes.deleteContact, payload: contact.userId });
    }
  };

  const handleRefresh = async () => {
    handleClose();
    // const newContact = await model.createContact(contact.userId);
    // if (newContact === undefined) {
    //   // todo: handle fail
    //   return;
    // }

    const isSuccess = await model.updateContactCreatedAt(contact.userId);
    if (!isSuccess) {
      // todo: handle fail
      return;
    }

    dispatch({ type: ActionTypes.addNewContact, payload: { ...contact, created_at: Date.now() } });
    console.log('refresh contact success');
  };

  const handleUsernameSubmit = async () => {
    try {
      // check need update or not
      if (username.length === 0 || username === usernameDefault) {
        setEdittingUsername(false);
        return;
      }

      // fetch
      setIsProgress(true);
      const isSuccess = await model.setContactUsername(contact.userId, username);
      setIsProgress(false);

      if (!isSuccess) {
        // todo: handle fail
        return;
      }

      // set username contact success, so update the context by dispatching
      dispatch({ type: ActionTypes.addNewContact, payload: { ...contact, username } });

      setEdittingUsername(false);
    } catch (error) {
      console.log(error);
    }
  };

  const getDateString = (time: number) => {
    const date = new Date(time);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(APP.locale, options);
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="edit-dialog"
      open={open}
      scroll="body"
      transitionDuration={0}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>{BUTTON.edit}</Box>
          <Box>
            {/* close button with autoFocus*/}
            <IconButton autoFocus onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {/* info section */}
      <Box border={1} borderRadius={5} borderColor="grey.500" mx={1.5} p={2}>
        <SectionTitle title={DIALOG.contact} />

        {!isEdittingUsername && (
          <>
            <Box display="inline" fontSize={15}>
              {usernameDefault}
            </Box>
            <IconButton aria-label="edit" onClick={() => setEdittingUsername(true)}>
              <EditIcon fontSize="inherit" />
            </IconButton>
          </>
        )}

        {/* username input */}
        {isEdittingUsername && (
          <>
            <MyTextField
              text={username}
              placeholder={usernameDefault}
              warning={''}
              isError={false}
              onChange={handleChange}
              onEnterPress={handleUsernameSubmit}
            />
            <MyButton
              isProgress={isProgress}
              onClick={handleUsernameSubmit}
              text={isProgress ? BUTTON.saving : BUTTON.submit}
            />
          </>
        )}
      </Box>
      <Box pb={1} />

      {/* refresh section */}
      <Box border={1} borderRadius={5} borderColor="grey.500" mx={1.5} p={2}>
        <SectionTitle title={DIALOG.lastContactDate} />
        <Box fontSize={15}>{getDateString(contact.created_at)}</Box>
        <HintText text={DIALOG.contactRefresh} />
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={handleRefresh} style={{ boxShadow: 'none' }}>
            {BUTTON.refreshContact}{' '}
          </Button>
        </Box>
      </Box>
      <Box pb={1} />

      {/* delete section */}
      <Box border={1} borderRadius={5} borderColor="grey.500" mx={1.5} p={2}>
        <HintText text={DIALOG.contactDelete}></HintText>
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={handleDelete} style={{ boxShadow: 'none' }}>
            {BUTTON.delete}
          </Button>
        </Box>
      </Box>
      <Box pb={1.5} />
    </Dialog>
  );
};
